import * as Sentry from '@sentry/react';
import { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { api } from 'src/common/utils/api';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ChatFile } from '../../chats/types';
import {
  ChatMessage,
  ChatMode,
  ChatState,
  FinalChunkData,
  isChatMessage,
  Message,
  MessageAttachment,
  MessageContent,
  MessageDirection,
  MessageState,
  PresentedFile,
  ProposedAction,
  ProposedActionStatus,
  StoredMessage,
} from '../types';
import { StreamOutcome, useChatTransport } from './useChatTransport';

type StateChangeCallback = (state: ChatState, data?: FinalChunkData) => void;

type ClientChatOptions = {
  mode?: typeof ChatMode.CLIENT;
  onStateChange?: StateChangeCallback;
};

type DocumentChatOptions = {
  mode: typeof ChatMode.DOCUMENT;
  entityId: string;
  requestBodyBuilder: (
    message: string,
    conversationId: string,
    verify?: { sectionPath?: string[] },
  ) => object;
  onStateChange?: StateChangeCallback;
};

type FamilyChatOptions = {
  mode: typeof ChatMode.FAMILY;
  familyId: string;
  // The member the advisor is currently viewing; bare questions default to them. Null/omitted on
  // the whole-household view.
  focusedClientId?: string | null;
  onStateChange?: StateChangeCallback;
};

type UseChatMessagesOptions = ClientChatOptions | DocumentChatOptions | FamilyChatOptions;

type ModeConfig = {
  handledStates: ChatState[];
  fireTransitionCallbacks: boolean;
};

const MODE_CONFIGS: Record<ChatMode, ModeConfig> = {
  [ChatMode.CLIENT]: {
    handledStates: [
      ChatState.THINKING,
      ChatState.ANSWERING,
      ChatState.TOOL_STARTED,
      ChatState.TOOL_FINISHED,
      ChatState.TOOL_LIMIT_REACHED,
      ChatState.FILE_PRESENTED,
      ChatState.ACTION_PROPOSED,
    ],
    fireTransitionCallbacks: false,
  },
  [ChatMode.FAMILY]: {
    handledStates: [
      ChatState.THINKING,
      ChatState.ANSWERING,
      ChatState.TOOL_STARTED,
      ChatState.TOOL_FINISHED,
      ChatState.TOOL_LIMIT_REACHED,
      ChatState.FILE_PRESENTED,
      ChatState.ACTION_PROPOSED,
    ],
    fireTransitionCallbacks: false,
  },
  [ChatMode.DOCUMENT]: {
    handledStates: [ChatState.THINKING, ChatState.ANSWERING, ChatState.EDITING, ChatState.FINAL],
    fireTransitionCallbacks: true,
  },
};

const createUserMessage = (content: string, attachments?: MessageAttachment[]): ChatMessage => {
  const response = [{ state: 'SENT', content }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id: uuidv4(),
    response,
    sender: 'Me',
    direction: MessageDirection.Outgoing,
    thinkingContent,
    answerContent,
    currentState,
    ...(attachments?.length ? { attachments } : {}),
  };
};

const createResponseMessage = (content?: string, id?: string): ChatMessage => {
  const response = content ? [{ state: 'ANSWERING', content }] : [];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id: id || uuidv4(),
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const createThinkingPlaceholderMessage = (id: string): ChatMessage => {
  const response: MessageContent[] = [{ state: ChatState.THINKING, content: '' }];
  const { thinkingContent, answerContent, currentState } = computeMessageProperties(response);

  return {
    kind: 'message',
    id,
    response,
    sender: 'Siesta',
    direction: MessageDirection.Incoming,
    thinkingContent,
    answerContent,
    currentState,
  };
};

const rawMessageToChatMessages = (raw: StoredMessage): Message[] => {
  if (typeof raw.content !== 'string' || !raw.content.trim()) return [];
  if (raw.role === 'user') return [createUserMessage(raw.content, raw.attachments)];
  if (raw.role === 'assistant') return [createResponseMessage(raw.content)];
  return [];
};

const findChatMessageById = (messages: Message[], id: string): ChatMessage | undefined =>
  messages.find((msg): msg is ChatMessage => msg.id === id && isChatMessage(msg));

const isEmptyThinkingPlaceholder = (message: Message): boolean =>
  isChatMessage(message) &&
  message.direction === MessageDirection.Incoming &&
  message.response.length === 1 &&
  message.response[0].state === ChatState.THINKING &&
  message.response[0].content === '' &&
  !message.currentToolName &&
  message.currentState !== ChatState.TOOL_LIMIT_REACHED;

type ParsedChunk = {
  state: MessageState;
  content: string;
  data?: FinalChunkData;
};

const parseStreamChunk = (rawData: string): ParsedChunk | null => {
  try {
    const parsed = JSON.parse(rawData);
    return {
      state: parsed.state,
      content: parsed.content,
      data: parsed.data,
    };
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
};

const processContentTransforms = (content: string): string => {
  const transforms = [
    {
      regex: /<span\s+[^>]*style="[^"]*"[^>]*>(.*?)<\/span>/gi,
      replacement: '$1',
    },

    // Answer content is sometimes incorrectly wrapped in a <response> tag
    { regex: /<response>(.*?)<\/response>/gis, replacement: '$1' },
  ];

  return transforms.reduce(
    (transformed, { regex, replacement }) => transformed.replace(regex, replacement),
    content,
  );
};

const computeMessageProperties = (
  response: MessageContent[],
): {
  thinkingContent: string;
  answerContent: string;
  currentState: MessageState;
} => {
  const thinkingContent = processContentTransforms(
    response
      .filter(r => r.state !== 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const answerContent = processContentTransforms(
    response
      .filter(r => r.state === 'ANSWERING')
      .map(r => r.content)
      .join(''),
  );

  const currentState = response[response.length - 1]?.state || 'SENT';

  return { thinkingContent, answerContent, currentState };
};

const appendToResponse = (
  message: ChatMessage,
  state: MessageState,
  content: string,
): ChatMessage => {
  const transformedContent = processContentTransforms(content);
  const newResponse = [...message.response];

  const lastResponse = newResponse[newResponse.length - 1];
  if (lastResponse && lastResponse.state === state) {
    newResponse[newResponse.length - 1] = {
      ...lastResponse,
      content: lastResponse.content + transformedContent,
    };
  } else {
    newResponse.push({ state, content: transformedContent });
  }

  const { thinkingContent, answerContent, currentState } = computeMessageProperties(newResponse);

  return {
    ...message,
    response: newResponse,
    thinkingContent,
    answerContent,
    currentState,
    // The retry notice is transient: once real answer content streams in, drop it.
    retryNotice: state === ChatState.ANSWERING ? undefined : message.retryNotice,
  };
};

export function useChatMessages(options?: UseChatMessagesOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [latestIncomingId, setLatestIncomingId] = useState<string | null>(null);
  const [currentChatState, setCurrentChatState] = useState<ChatState | null>(null);
  const chatStateRef = useRef<ChatState | null>(null);
  const {
    streamRequest,
    subscribeToTurn,
    cancelTurn,
    abort: abortTransport,
    buildClientChatUrl,
    buildCrossClientChatUrl,
    buildFamilyChatUrl,
    buildDocumentEditUrl,
    buildTurnStreamUrl,
  } = useChatTransport();

  // The background turn currently streaming into the open chat, so the stop button can tell the
  // worker which turn to cancel. Null on the live/document paths, which have no cancellable turn.
  const activeTurnRef = useRef<{ conversationId: string; turnId: string } | null>(null);

  const { flagEnabledSiestaChats, flagEnabledSiestaAsyncGeneration } = useFlags();
  const backgroundGenerationEnabled = flagEnabledSiestaChats && flagEnabledSiestaAsyncGeneration;

  const mode = options?.mode ?? ChatMode.CLIENT;
  const modeConfig = MODE_CONFIGS[mode];
  const onStateChangeRef = useRef(options?.onStateChange);
  onStateChangeRef.current = options?.onStateChange;

  const modeConfigRef = useRef(modeConfig);
  modeConfigRef.current = modeConfig;

  // The document id for the in-flight verify, captured at send time. The FINAL handler runs from a
  // memoised stream callback whose `options` closure can be stale, so it can't read entityId itself.
  const verifyMeetingNoteIdRef = useRef<string | undefined>(undefined);

  const updateChatState = useCallback((newState: ChatState | null) => {
    chatStateRef.current = newState;
    setCurrentChatState(newState);
  }, []);

  const addMessage = useCallback((message: ChatMessage): void => {
    setMessages(prev => [...prev, message]);
    if (message.direction === MessageDirection.Incoming) {
      setLatestIncomingId(message.id);
    }
  }, []);

  const handleIncomingStreamChunk = useCallback(
    (messageId: string, rawData: string): void => {
      if (chatStateRef.current === null) return;

      const parsedChunk = parseStreamChunk(rawData);
      if (!parsedChunk) return;

      const { state, content, data } = parsedChunk;
      const config = modeConfigRef.current;

      if (state === ChatState.EDITING) {
        if (!config.handledStates.includes(ChatState.EDITING)) return;
        if (chatStateRef.current !== ChatState.EDITING) {
          updateChatState(ChatState.EDITING);
          onStateChangeRef.current?.(ChatState.EDITING);
        }
        return;
      }

      if (state === ChatState.FINAL) {
        if (!config.handledStates.includes(ChatState.FINAL)) return;
        updateChatState(ChatState.FINAL);
        onStateChangeRef.current?.(ChatState.FINAL, data);

        if (data?.verifyOutcome) {
          const meetingNoteId = verifyMeetingNoteIdRef.current;
          setMessages(currentMessages => {
            const target = findChatMessageById(currentMessages, messageId);
            if (!target) return currentMessages;
            const updated = {
              ...target,
              // Leave the thinking-placeholder state behind so the post-stream cleanup doesn't
              // filter this message out — VerifyResult renders off verifyOutcome, not the response.
              response: [{ state: ChatState.ANSWERING, content: '' }],
              currentState: ChatState.ANSWERING,
              verifyOutcome: data.verifyOutcome,
              evidence: data.evidence ?? [],
              verifyMeetingNoteId: meetingNoteId,
              verifyNote: data.verifyNote,
            };
            return currentMessages.map(msg => (msg.id === messageId ? updated : msg));
          });
          return;
        }

        if (data?.assistantMessage) {
          setMessages(currentMessages => {
            const targetMessage = findChatMessageById(currentMessages, messageId);
            if (!targetMessage || targetMessage.answerContent.trim()) return currentMessages;
            const updatedMessage = appendToResponse(
              targetMessage,
              ChatState.ANSWERING,
              data.assistantMessage,
            );
            return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
          });
          return;
        }

        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage || !isEmptyThinkingPlaceholder(targetMessage)) return currentMessages;
          return currentMessages.filter(msg => msg.id !== messageId);
        });
        setLatestIncomingId(prev => (prev === messageId ? null : prev));
        return;
      }

      if (state === ChatState.RETRY_NOTICE) {
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) {
            const newMessage = createResponseMessage();
            newMessage.id = messageId;
            newMessage.retryNotice = content;
            setLatestIncomingId(messageId);
            return [...currentMessages, newMessage];
          }
          return currentMessages.map(msg =>
            msg.id === messageId ? { ...target, retryNotice: content } : msg,
          );
        });
        return;
      }

      if (state === ChatState.FILE_PRESENTED) {
        let file: PresentedFile;
        try {
          file = JSON.parse(content) as PresentedFile;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? { ...target, presentedFiles: [...(target.presentedFiles ?? []), file] }
              : msg,
          );
        });
        return;
      }

      // Must early-return like FILE_PRESENTED: the content is a JSON payload for the approval
      // widget, and falling through would render it as answer text.
      if (state === ChatState.ACTION_PROPOSED) {
        let proposal: ProposedAction;
        try {
          proposal = JSON.parse(content) as ProposedAction;
        } catch (e) {
          Sentry.captureException(e);
          return;
        }
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  proposedActions: [
                    ...(target.proposedActions ?? []).filter(
                      p => p.proposalId !== proposal.proposalId,
                    ),
                    { ...proposal, status: proposal.status ?? 'PENDING' },
                  ],
                }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.TOOL_LIMIT_REACHED) {
        setMessages(currentMessages => {
          const target = findChatMessageById(currentMessages, messageId);
          if (!target) return currentMessages;
          return currentMessages.map(msg =>
            msg.id === messageId
              ? {
                  ...target,
                  currentState: ChatState.TOOL_LIMIT_REACHED,
                  currentToolName: undefined,
                }
              : msg,
          );
        });
        return;
      }

      if (state === ChatState.TOOL_STARTED || state === ChatState.TOOL_FINISHED) {
        updateChatState(
          state === ChatState.TOOL_STARTED ? ChatState.THINKING : ChatState.ANSWERING,
        );
        setMessages(currentMessages => {
          const targetMessage = findChatMessageById(currentMessages, messageId);
          if (!targetMessage) return currentMessages;
          const label = state === ChatState.TOOL_STARTED ? `${content}...` : '';
          const updatedMessage = { ...targetMessage, currentToolName: label };
          return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
        });
        return;
      }

      if (state === ChatState.THINKING && chatStateRef.current !== ChatState.THINKING) {
        updateChatState(ChatState.THINKING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.THINKING);
        }
      } else if (state === ChatState.ANSWERING && chatStateRef.current !== ChatState.ANSWERING) {
        updateChatState(ChatState.ANSWERING);
        if (config.fireTransitionCallbacks) {
          onStateChangeRef.current?.(ChatState.ANSWERING);
        }
      }

      setMessages(currentMessages => {
        const targetMessage = findChatMessageById(currentMessages, messageId);
        if (!targetMessage) {
          const newMessage = createResponseMessage();
          newMessage.id = messageId;
          const updatedMessage = appendToResponse(newMessage, state, content);
          setLatestIncomingId(messageId);
          return [...currentMessages, updatedMessage];
        }

        const updatedMessage = appendToResponse(targetMessage, state, content);
        return currentMessages.map(msg => (msg.id === messageId ? updatedMessage : msg));
      });
    },
    [updateChatState],
  );

  const removeEmptyPlaceholderAndReset = useCallback(() => {
    setLatestIncomingId(null);
    setMessages(prev => prev.filter(msg => !isEmptyThinkingPlaceholder(msg)));
    if (modeConfigRef.current.fireTransitionCallbacks) {
      onStateChangeRef.current?.(ChatState.CANCELLED);
    }
    updateChatState(null);
  }, [updateChatState]);

  const runStream = useCallback(
    async (
      invokeStream: (
        onChunk: (rawData: string) => void,
        onError: () => void,
      ) => Promise<StreamOutcome>,
      receivedMessageId: string,
    ): Promise<void> => {
      const outcome = await invokeStream(
        rawData => handleIncomingStreamChunk(receivedMessageId, rawData),
        () => {
          removeEmptyPlaceholderAndReset();
          addMessage(
            createResponseMessage(
              'An error occurred while processing your request, please wait a moment and try again. If the issue persists, please contact support.',
            ),
          );
        },
      );

      // Switching to another conversation aborts this run. Since the chat hook is shared across
      // conversations, resetting here would wipe the state of whichever conversation is now open
      // (dropping its live chunks via the chatState===null guard); the aborting caller already
      // reset what it needed to.
      if (outcome.aborted) return;

      setMessages(prev => prev.filter(msg => !isEmptyThinkingPlaceholder(msg)));
      updateChatState(null);
      activeTurnRef.current = null;
    },
    [handleIncomingStreamChunk, updateChatState, addMessage, removeEmptyPlaceholderAndReset],
  );

  const fetchAndHandleStreamResponse = useCallback(
    (url: string, body: object, receivedMessageId: string): Promise<void> =>
      runStream(
        (onChunk, onError) => streamRequest(url, body, onChunk, onError),
        receivedMessageId,
      ),
    [runStream, streamRequest],
  );

  const streamExistingTurn = useCallback(
    async (conversationId: string, turnId: string, receivedMessageId: string): Promise<void> => {
      await runStream(
        (onChunk, onError) =>
          subscribeToTurn(buildTurnStreamUrl(conversationId, turnId), onChunk, onError),
        receivedMessageId,
      );
      // The turn has finished streaming into the open chat, so the user has now seen it.
      api
        .put(`conversations/${conversationId}/read`)
        .catch(error => Sentry.captureException(error));
    },
    [runStream, subscribeToTurn, buildTurnStreamUrl],
  );

  const generateInBackground = useCallback(
    async (
      content: string,
      clientId: string | null,
      conversationId: string,
      messageId: string,
      attachmentIds: string[],
    ): Promise<void> => {
      let turnId: string;
      try {
        const { data } = await api.post<{ turnId: string }>(
          `conversations/${conversationId}/turns`,
          { message: content, clientId: clientId ?? undefined, attachmentIds },
        );
        turnId = data.turnId;
      } catch (error) {
        // Enqueue rejected (e.g. the async-generation flag is off server-side): fall back to
        // the live streaming path so the user still gets a response.
        Sentry.captureException(error);
        const url = clientId ? buildClientChatUrl(clientId) : buildCrossClientChatUrl();
        await fetchAndHandleStreamResponse(
          url,
          { conversationId, message: content, attachmentIds },
          messageId,
        );
        return;
      }

      activeTurnRef.current = { conversationId, turnId };
      await streamExistingTurn(conversationId, turnId, messageId);
    },
    [streamExistingTurn, fetchAndHandleStreamResponse, buildClientChatUrl, buildCrossClientChatUrl],
  );

  const resumeActiveTurn = useCallback(
    (conversationId: string, turnId: string): void => {
      if (chatStateRef.current !== null) return;

      updateChatState(ChatState.THINKING);
      if (modeConfigRef.current.fireTransitionCallbacks) {
        onStateChangeRef.current?.(ChatState.THINKING);
      }

      const messageId = uuidv4();
      addMessage(createThinkingPlaceholderMessage(messageId));
      activeTurnRef.current = { conversationId, turnId };
      void streamExistingTurn(conversationId, turnId, messageId);
    },
    [streamExistingTurn, updateChatState, addMessage],
  );

  const sendUserMessage = useCallback(
    async (
      content: string,
      clientId: string | null,
      conversationId: string,
      attachedFiles: ChatFile[] = [],
      verify?: { sectionPath?: string[] },
      silent = false,
    ): Promise<void> => {
      if (chatStateRef.current !== null) {
        Sentry.addBreadcrumb({
          category: 'siesta',
          level: 'warning',
          message: 'sendUserMessage dropped: stream already in flight',
          data: { chatState: chatStateRef.current },
        });
        return;
      }

      updateChatState(ChatState.THINKING);
      if (modeConfigRef.current.fireTransitionCallbacks) {
        onStateChangeRef.current?.(ChatState.THINKING);
      }

      const attachments = attachedFiles.map(file => ({
        filename: file.filename,
        extension: file.extension,
      }));
      const attachmentIds = attachedFiles.map(file => file.id);

      const messageId = uuidv4();
      // System-initiated sends (verify, and the verify hand-off) are silent — the instruction
      // isn't something the user typed, so only the assistant's reply and thinking state show.
      if (!verify && !silent) addMessage(createUserMessage(content, attachments));
      addMessage(createThinkingPlaceholderMessage(messageId));

      try {
        if (mode === ChatMode.DOCUMENT) {
          const docOptions = options as DocumentChatOptions;
          if (verify) verifyMeetingNoteIdRef.current = docOptions.entityId;
          const body = docOptions.requestBodyBuilder(content, conversationId, verify);
          await fetchAndHandleStreamResponse(
            buildDocumentEditUrl(docOptions.entityId),
            body,
            messageId,
          );
          return;
        }

        if (mode === ChatMode.FAMILY) {
          const familyOptions = options as FamilyChatOptions;
          // A member-focused family chat can background-generate: the worker resolves the
          // household from the focused member, so the turn covers the family and survives
          // disconnects like any client turn. The whole-household view streams live — the turn
          // request has no family reference to resolve from.
          if (backgroundGenerationEnabled && familyOptions.focusedClientId) {
            await generateInBackground(
              content,
              familyOptions.focusedClientId,
              conversationId,
              messageId,
              attachmentIds,
            );
            return;
          }
          await fetchAndHandleStreamResponse(
            buildFamilyChatUrl(familyOptions.familyId),
            {
              conversationId,
              message: content,
              attachmentIds,
              focusedClientId: familyOptions.focusedClientId ?? null,
            },
            messageId,
          );
          return;
        }

        if (backgroundGenerationEnabled) {
          await generateInBackground(content, clientId, conversationId, messageId, attachmentIds);
          return;
        }

        const url = clientId ? buildClientChatUrl(clientId) : buildCrossClientChatUrl();
        await fetchAndHandleStreamResponse(
          url,
          { conversationId, message: content, attachmentIds },
          messageId,
        );
      } catch (error) {
        Sentry.captureException(error);
        removeEmptyPlaceholderAndReset();
      }
    },
    [
      mode,
      options,
      backgroundGenerationEnabled,
      generateInBackground,
      fetchAndHandleStreamResponse,
      addMessage,
      buildClientChatUrl,
      buildCrossClientChatUrl,
      buildFamilyChatUrl,
      buildDocumentEditUrl,
      updateChatState,
      removeEmptyPlaceholderAndReset,
    ],
  );

  const cancelStreaming = useCallback(() => {
    abortTransport();
    removeEmptyPlaceholderAndReset();
    activeTurnRef.current = null;
  }, [abortTransport, removeEmptyPlaceholderAndReset]);

  // The explicit stop button: tell the worker to halt this turn's generation, then drop the
  // local subscription. The partial answer already streamed in stays on screen.
  const stopGeneration = useCallback(() => {
    const active = activeTurnRef.current;
    if (active) {
      void cancelTurn(active.conversationId, active.turnId);
    }
    cancelStreaming();
  }, [cancelTurn, cancelStreaming]);

  const clearMessages = useCallback(() => {
    cancelStreaming();
    setMessages([]);
  }, [cancelStreaming]);

  const loadHistory = useCallback(
    (rawMessages: StoredMessage[]) => {
      abortTransport();
      updateChatState(null);
      setLatestIncomingId(null);
      setMessages(rawMessages.flatMap(rawMessageToChatMessages));
    },
    [abortTransport, updateChatState],
  );

  const replaceLastAssistantMessage = useCallback((content: string) => {
    setMessages(prev => {
      const lastIncomingIdx = [...prev]
        .reverse()
        .findIndex(m => isChatMessage(m) && m.direction === MessageDirection.Incoming);
      if (lastIncomingIdx === -1) return prev;
      const idx = prev.length - 1 - lastIncomingIdx;
      const replacement = createResponseMessage(content, prev[idx].id);
      return [...prev.slice(0, idx), replacement, ...prev.slice(idx + 1)];
    });
  }, []);

  const appendInterruptedMarker = useCallback(() => {
    setMessages(prev => [...prev, { kind: 'interrupted', id: uuidv4() }]);
  }, []);

  const markProposalDecided = useCallback(
    (
      messageId: string,
      proposalId: string,
      status: ProposedActionStatus,
      failureReason?: string | null,
    ) => {
      setMessages(prev =>
        prev.map(msg => {
          if (msg.id !== messageId || !isChatMessage(msg) || !msg.proposedActions) return msg;
          return {
            ...msg,
            proposedActions: msg.proposedActions.map(proposal =>
              proposal.proposalId === proposalId
                ? { ...proposal, status, failureReason: failureReason ?? proposal.failureReason }
                : proposal,
            ),
          };
        }),
      );
    },
    [],
  );

  const finaliseLimitReachedMessage = useCallback((id: string) => {
    setMessages(prev =>
      prev.flatMap(msg => {
        if (msg.id !== id || !isChatMessage(msg)) return [msg];
        const hasPrePauseContent = Boolean(msg.answerContent.trim() || msg.thinkingContent.trim());
        if (!hasPrePauseContent) return [];
        return [{ ...msg, currentState: ChatState.ANSWERING }];
      }),
    );
  }, []);

  return {
    messages,
    sendUserMessage,
    resumeActiveTurn,
    latestIncomingId,
    clearMessages,
    loadHistory,
    replaceLastAssistantMessage,
    appendInterruptedMarker,
    finaliseLimitReachedMessage,
    markProposalDecided,
    currentChatState,
    cancelStreaming,
    stopGeneration,
  };
}
